import { useRef } from 'react'
import { Segments } from '@/components/ui'
import { manifesto } from '@/content/site'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { EASE, gsap, ScrollTrigger, SplitText, useGSAP } from '@/lib/gsap'
import styles from './Manifesto.module.css'

const TITLE_ID = 'manifesto-versus-title'
/** Minimum gap between rows starting, in seconds: a reply, not a list dump. */
const TURN = 0.42

/**
 * "Not another dashboard": a real two-column table. Row by row, the old way is struck
 * through and recedes to muted, then the Vokarta side answers, rising in behind an iris
 * dot that sends out a single voice pulse. Rows queue with a conversational rhythm even
 * when you scroll fast. Without motion every row shows its final, readable state.
 */
export function Versus() {
  const rootRef = useRef<HTMLDivElement>(null)
  const reducedMotion = useReducedMotion()

  useGSAP(
    (_context, contextSafe) => {
      const root = rootRef.current
      if (!root || !contextSafe) return
      const title = root.querySelector<HTMLElement>('[data-versus-title]')
      const table = root.querySelector<HTMLElement>('[data-versus-table]')
      const rows = gsap.utils.toArray<HTMLElement>('[data-row]', root)

      if (reducedMotion) {
        gsap.from([title, table], {
          opacity: 0,
          duration: 0.3,
          ease: 'none',
          scrollTrigger: { trigger: root, start: 'top 85%', once: true },
        })
        return
      }

      if (title) {
        SplitText.create(title, {
          type: 'lines',
          mask: 'lines',
          tag: 'span',
          aria: 'none',
          autoSplit: true,
          onSplit: (self) =>
            gsap.from(self.lines, {
              yPercent: 105,
              duration: 1.3,
              stagger: 0.09,
              ease: EASE.out,
              scrollTrigger: { trigger: title, start: 'top 84%', once: true },
            }),
        })
      }

      const computed = getComputedStyle(root)
      const ink = computed.getPropertyValue('--color-fg').trim()
      const muted = computed.getPropertyValue('--color-fg-muted').trim()
      let nextTurn = 0
      const speak = contextSafe((timeline: gsap.core.Timeline) => {
        const now = gsap.ticker.time
        const start = Math.max(now, nextTurn)
        nextTurn = start + TURN
        gsap.delayedCall(start - now, () => {
          timeline.play()
        })
      })

      rows.forEach((row) => {
        const from = row.querySelector('[data-from]')
        const dot = row.querySelector('[data-dot]')
        const pulse = row.querySelector('[data-pulse]')
        const to = row.querySelector('[data-to]')
        gsap.set(from, { color: ink, '--strike': '0%' })
        gsap.set(dot, { scale: 0 })
        gsap.set(pulse, { scale: 0.3, opacity: 0 })
        gsap.set(to, { opacity: 0, y: '0.5em' })
        const timeline = gsap
          .timeline({ paused: true })
          .to(from, { '--strike': '100%', duration: 0.9, ease: EASE.inOut }, 0)
          .to(from, { color: muted, duration: 1.1, ease: EASE.out }, 0.32)
          .to(dot, { scale: 1, duration: 0.8, ease: EASE.out }, 0.58)
          .fromTo(
            pulse,
            { scale: 0.3, opacity: 0.85 },
            { scale: 3.4, opacity: 0, duration: 1.6, ease: 'power2.out' },
            0.6,
          )
          .to(to, { opacity: 1, y: 0, duration: 1.3, ease: EASE.out }, 0.7)
        ScrollTrigger.create({
          trigger: row,
          start: 'top 84%',
          once: true,
          onEnter: () => speak(timeline),
        })
      })
    },
    { scope: rootRef, dependencies: [reducedMotion], revertOnUpdate: true },
  )

  return (
    <div ref={rootRef} className={styles.versus}>
      <h3 id={TITLE_ID} className={styles.versusTitle} data-versus-title>
        <Segments segments={manifesto.versus.title} />
      </h3>
      <table className={styles.table} aria-labelledby={TITLE_ID} data-versus-table>
        <thead>
          <tr>
            <th scope="col" className={styles.th}>
              {manifesto.versus.from}
            </th>
            <th scope="col" className={styles.th}>
              <span className={styles.thDot} aria-hidden="true" />
              {manifesto.versus.to}
            </th>
          </tr>
        </thead>
        <tbody>
          {manifesto.versus.rows.map(([from, to]) => (
            <tr key={from} className={styles.row} data-row>
              <td className={styles.fromCell}>
                <span className={styles.fromText} data-from>
                  {from}
                </span>
              </td>
              <td className={styles.toCell}>
                <span className={styles.toInner}>
                  <span className={styles.voice} aria-hidden="true">
                    <span className={styles.pulse} data-pulse />
                    <span className={styles.voiceDot} data-dot />
                  </span>
                  <span className={styles.toText} data-to>
                    {to}
                  </span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
